import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Clock,
  DollarSign,
  Users,
  Rocket,
  CheckCircle2,
  Upload,
  Linkedin,
  Github,
  Phone,
  Mail,
  Diamond,
  User,
  IndianRupee,
} from "lucide-react";
import { jobApi } from "../data/JobData";
import { toast } from "react-toastify";

const extractListItemsFromHtml = (html = "") => {
  // takes "<ul><li>A</li><li>B</li></ul>" -> ["A", "B"]
  const doc = new DOMParser().parseFromString(html, "text/html");
  const lis = Array.from(doc.querySelectorAll("li"));

  return lis
    .map((li) => (li.textContent || "").replace(/\u00A0/g, " ").trim()) // \u00A0 = nbsp
    .map((t) => t.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

const SectionCard = ({ title, icon: Icon, items }) => (
  <div className="bg-[#F7FBFF] border border-[#E1ECF5] rounded-3xl p-8 shadow-[0_18px_45px_rgba(15,23,42,0.04)]">
    <div className="flex items-center gap-3 mb-4">
      <div className="bg-[#E6F4FB] text-[#2A9CD7] p-3 rounded-xl">
        <Icon size={22} />
      </div>
      <h3 className="text-3xl font-semibold text-[#093A5B]">{title}</h3>
    </div>

    <ul className="space-y-3">
      {items?.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-[#093A5B]/90">
          <CheckCircle2 className="mt-0.5 text-[#2A9CD7]" size={18} />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function JobDetails() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin: "",
    github: "",
    resume: null,
    current_location: "",
    experience_years: "",
    why_join: "",
    // cover_letter: "",
  });
  const [errors, setErrors] = useState({});
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();

  // Fetch job details
  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await jobApi.getJobById(id);
      setJob(response.data.data);
      // console.log("Resp", response.data.data);
    } catch (err) {
      console.error("Error fetching job details:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load job details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Validate fields
  const validate = () => {
    let newErrors = {};

    if (!form.full_name.trim()) newErrors.full_name = "Full name is required.";
    if (!form.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address.";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (form.phone.length < 10) {
      newErrors.phone = "Phone must be at least 10 digits.";
    }

    if (!form.resume) newErrors.resume = "Please upload your resume.";
    if (!form.current_location.trim())
      newErrors.current_location = "Current location is required.";
    if (!form.experience_years.trim())
      newErrors.experience_years = "Experience years is required.";
    if (!form.why_join.trim()) newErrors.why_join = "Why join is required.";
    return newErrors;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validate();
    setErrors(validation);

    if (Object.keys(validation).length === 0) {
      try {
        setSubmitting(true);

        // Prepare form data
        const formData = new FormData();
        formData.append("full_name", form.full_name);
        formData.append("email", form.email);
        formData.append("phone", form.phone);
        formData.append("linkedin", form.linkedin);
        formData.append("github", form.github);
        formData.append("current_location", form.current_location);
        formData.append("experience_years", form.experience_years);
        formData.append("why_join", form.why_join);
        // formData.append('cover_letter', form.cover_letter);
        formData.append("job_id", id);
        if (form.resume) {
          formData.append("resume", form.resume);
        }

        // Submit application
        await jobApi.applyForJob(id, formData);

        // alert("Application submitted successfully!");
        toast.success("Application submitted successfully!");
        navigate("/careers");

        // Reset form
        setForm({
          full_name: "",
          email: "",
          phone: "",
          linkedin: "",
          github: "",
          resume: null,
          current_location: "",
          experience_years: "",
          why_join: "",
          // cover_letter: "",
        });
      } catch (err) {
        console.error("Error submitting application:", err);
        alert(
          err.response?.data?.message ||
            "Failed to submit application. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Update form + remove error when typing
  const updateField = (field, value) => {
    setForm({ ...form, [field]: value });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, resume: "File size should be less than 5MB" });
        return;
      }

      // Check file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors({
          ...errors,
          resume: "Only PDF and DOC/DOCX files are allowed",
        });
        return;
      }

      updateField("resume", file);
    }
  };
  const formatINR = (value) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-IN");
  };

  if (loading) {
    return (
      <div className="w-full py-20 px-4 lg:px-16 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A9CD7] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="w-full py-20 px-4 lg:px-16 bg-gray-50 min-h-screen">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-red-500 mb-4 p-4 bg-red-50 rounded-lg">
            <p className="font-semibold">Error Loading Job Details</p>
            <p className="text-sm mt-1">{error || "Job not found"}</p>
          </div>
          <button
            onClick={() => navigate("/careers")}
            className="px-6 py-3 bg-[#2A9CD7] text-white rounded-lg hover:bg-[#093A5B] transition-colors font-medium flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={20} />
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pb-24 bg-[#F9FBFD]">
      {/* ===== HEADER ===== */}
      {/* sticky top-0 z-999 */}
      <div className="bg-gradient-to-b from-[#093A5B] to-[#0E527A] text-white pt-4 pb-4 px-6 lg:px-16 ">
        <div className="max-w-6xl mx-auto">
          {/* Back */}
          <button
            onClick={() => navigate("/careers")}
            className="flex items-center gap-2 mb-6 text-white/80 hover:text-white transition"
          >
            <ArrowLeft size={18} />
            Back to All Positions
          </button>

          {/* Tag */}
          {/* inline-block bg-white/10 border border-white/20 text-[12px] tracking-wider uppercase px-4 py-1 rounded-full carrers_job_heading */}
          <span className="carrers_job_heading">{job.heading}</span>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold mt-4">{job.title}</h1>

          {/* Subtitle */}
          <p className="text-white/80 max-w-2xl mt-3 text-lg">
            {/* {job.shortDescription}what */}
            {job.subheading}
          </p>

          {/* Meta info */}
          <div className="flex flex-wrap gap-3 mt-4">
            <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg text-sm">
              <MapPin size={16} /> {job.location}
            </span>

            <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg text-sm">
              <Clock size={16} /> {job.mode}
            </span>

            <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg text-sm">
              <IndianRupee size={16} /> {formatINR(job.salary)}
            </span>

            <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg text-sm">
              <Users size={16} /> {job.category}
            </span>

            {/* <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
              <Rocket size={16} /> Start: {job.start}
            </span> */}
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 px-4 lg:px-16 mt-14">
        {/* LEFT SIDE CONTENT */}
        <div className="lg:col-span-1 space-y-8">
          {/* <SectionCard
            title={job.description_sections.title}

            icon={Rocket}
            // items={job.details?.responsibilities || []}
            items={
              (job?.description || []).length > 0
                ? job.description.map((item, index) =>
                  item.title
                    ? `${item.title}`
                    : item.content || 'No description'
                )
                : ["No responsibilities listed yet"]
            }
          /> */}
          {(job?.description_sections || []).map((sec) => (
            <SectionCard
              key={sec.id}
              title={sec.title}
              icon={Rocket}
              items={extractListItemsFromHtml(sec.content)}
            />
          ))}
          {/* {console.log("object", job.description_sections)} */}
          {/* {console.log("What you ll do", job.description_sections.title)} */}
          {/* <SectionCard
            title="What We're Looking For"
            icon={Users}
            items={job.details?.requirements || []}
          />

          <SectionCard
            title="Nice to Have"
            icon={CheckCircle2}
            items={[
              "Microservices knowledge",
              "DevOps experience",
              "Open source contributions",
              "Startup experience",
            ]}
          />

          <SectionCard
            title="What We Offer"
            icon={Diamond}
            items={[
              "Competitive salary + equity",
              "Premium medical, dental & vision",
              "Unlimited PTO & flexible work schedule",
              "$3,000 learning budget",
              "Remote-first culture",
              "Top-tier equipment & tools",
            ]}
          /> */}
        </div>

        {/* ===== APPLY FORM ===== */}
        <div className=" bg-white rounded-3xl p-8 border border-[#E1ECF5] shadow-[0_18px_45px_rgba(15,23,42,0.04)]">
          <h3 className="text-xl font-semibold text-[#093A5B]">
            Apply for this Position
          </h3>

          <form onSubmit={handleSubmit} className="space-y-5 mt-6">
            {/* NAME */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div
                className={`flex items-center gap-2 w-full mt-2 p-3 rounded-xl border 
                ${errors.full_name ? "border-red-400" : "border-[#D6E4EE]"} 
                hover:border-[#2A9CD7]`}
              >
                <User size={18} className="text-[#2A9CD7]" />
                <input
                  value={form.full_name}
                  placeholder="Enter Your Name"
                  onChange={(e) => updateField("full_name", e.target.value)}
                  className="w-full outline-none"
                />
              </div>
              {errors.full_name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* EMAIL */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div
                className={`flex items-center gap-2 w-full mt-2 p-3 rounded-xl border 
                ${errors.email ? "border-red-400" : "border-[#D6E4EE]"} 
                hover:border-[#2A9CD7]`}
              >
                <Mail size={18} className="text-[#2A9CD7]" />
                <input
                  type="email"
                  value={form.email}
                  placeholder="Enter Your Email"
                  onChange={(e) => updateField("email", e.target.value)}
                  className="w-full outline-none"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* PHONE */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div
                className={`flex items-center gap-2 w-full mt-2 p-3 rounded-xl border 
                ${errors.phone ? "border-red-400" : "border-[#D6E4EE]"} 
                hover:border-[#2A9CD7]`}
              >
                <Phone size={18} className="text-[#2A9CD7]" />
                <input
                  type="tel"
                  value={form.phone}
                  placeholder="Enter Your Phone Number"
                  maxLength={10}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      updateField("phone", value);
                    }
                  }}
                  className="w-full outline-none"
                />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            {/* LINKEDIN */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                LinkedIn Profile
              </label>
              <div className="flex items-center gap-2 w-full mt-2 p-3 rounded-xl border border-[#D6E4EE] hover:border-[#2A9CD7]">
                <Linkedin size={18} className="text-[#2A9CD7]" />
                <input
                  type="url"
                  value={form.linkedin}
                  placeholder="https://linkedin.com/in/yourprofile"
                  onChange={(e) => updateField("linkedin", e.target.value)}
                  className="w-full outline-none"
                />
              </div>
            </div>

            {/* GITHUB */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                GitHub / Portfolio
              </label>
              <div className="flex items-center gap-2 w-full mt-2 p-3 rounded-xl border border-[#D6E4EE] hover:border-[#2A9CD7]">
                <Github size={18} className="text-[#2A9CD7]" />
                <input
                  type="url"
                  value={form.github}
                  placeholder="https://github.com/yourusername"
                  onChange={(e) => updateField("github", e.target.value)}
                  className="w-full outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Current Location
                <span className="text-red-500">*</span>
              </label>
              <div
                className={`flex items-center gap-2 w-full mt-2 p-3 rounded-xl border ${errors.current_location ? "border-red-400" : "border-[#D6E4EE]"} hover:border-[#2A9CD7]`}
              >
                <MapPin size={18} className="text-[#2A9CD7]" />
                <input
                  value={form.current_location}
                  placeholder="Enter Your Current Location"
                  onChange={(e) =>
                    updateField("current_location", e.target.value)
                  }
                  className="w-full outline-none"
                />
              </div>
              {errors.current_location && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.current_location}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Experience Years <span className="text-red-500">*</span>
                <div
                  className={`flex items-center gap-2 w-full mt-2 p-3 rounded-xl border ${errors.experience_years ? "border-red-400" : "border-[#D6E4EE]"} hover:border-[#2A9CD7]`}
                >
                  <Clock size={18} className="text-[#2A9CD7]" />
                  <input
                    type="number"
                    value={form.experience_years}
                    placeholder="Enter Your Experience Years"
                    onChange={(e) =>
                      updateField("experience_years", e.target.value)
                    }
                    className="w-full outline-none"
                  />
                </div>
                {errors.experience_years && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.experience_years}
                  </p>
                )}
              </label>
            </div>

            {/* RESUME */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Resume / CV <span className="text-red-500">*</span>
              </label>

              <div
                className={`border rounded-xl mt-2 p-4 flex items-center justify-between cursor-pointer 
                ${
                  errors.resume
                    ? "border-red-400"
                    : "border-dashed border-[#D6E4EE]"
                } 
                hover:border-[#2A9CD7]`}
                onClick={() => document.getElementById("resumeInput").click()}
              >
                <span className="text-base text-gray-400 font-medium">
                  {form.resume
                    ? form.resume.name
                    : "Upload Your Resume (PDF/DOC)"}
                </span>
                <Upload size={20} className="text-[#2A9CD7]" />
              </div>

              <input
                id="resumeInput"
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
              />

              {errors.resume && (
                <p className="text-red-500 text-xs mt-1">{errors.resume}</p>
              )}
            </div>

            {/* COVER LETTER */}
            <div>
              <label className="text-sm font-medium text-[#093A5B]">
                Why Do You Want to Join?
              </label>
              <textarea
                value={form.why_join}
                placeholder="Tell us why you're a great fit for this role..."
                onChange={(e) => updateField("why_join", e.target.value)}
                className="w-full mt-2 p-3 rounded-xl border border-[#D6E4EE] h-28 hover:border-[#2A9CD7] outline-none"
              ></textarea>
            </div>

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full mt-6 bg-gradient-to-r from-[#093A5B] to-[#2A9CD7] text-white py-3 rounded-xl font-medium transition duration-300
                ${submitting ? "opacity-50 cursor-not-allowed" : "hover:scale-[0.98]"}`}
            >
              {submitting ? "Submitting..." : "Submit Application"}
            </button>

            <p className="text-xs text-[#093A5B]/60 text-center mt-4">
              By applying, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
